import type { ITranslator } from "@/interfaces/ITranslator.ts";
import type { ITranslationProvider } from "@/interfaces/ITranslationProvider.ts";
import type { IAppwriteException } from "@/interfaces/IAppwriteException.ts";

export class AppwriteExceptionTranslator implements ITranslator {
	private currentLanguage: string;

	constructor(private translationProvider: ITranslationProvider) {
		this.currentLanguage = translationProvider.getDefaultLanguage();
	}

	/**
	 * Translate an exception to a user-friendly message
	 */
	translate(exception: IAppwriteException, language?: string): string {
		const lang = language || this.currentLanguage;

		// Handle non-Appwrite errors or errors without a type
		if (!this.isAppwriteException(exception)) {
			return (exception as Error).message;
		}

		// First try to translate by error type (preferred method)
		if (exception.type) {
			const typeTranslation = this.translationProvider.getTranslation(
				exception.type,
				lang,
			);
			if (typeTranslation) {
				return typeTranslation;
			}
		}

		// Fall back to code-based translation if available
		if (exception.code) {
			const codeTranslation = this.translationProvider.getTranslation(
				exception.code,
				lang,
			);
			if (codeTranslation) {
				return codeTranslation;
			}
		}

		// If no translation is found, return the original message
		return exception.message;
	}

	/**
	 * Check if an error is likely an Appwrite exception
	 */
	private isAppwriteException(
		// biome-ignore lint/suspicious/noExplicitAny: The type of exception is not known
		exception: any,
	): exception is IAppwriteException {
		return (
			exception &&
			typeof exception.message === "string" &&
			(typeof exception.type === "string" || typeof exception.code === "number")
		);
	}

	setLanguage(language: string): void {
		if (this.translationProvider.getAvailableLanguages().includes(language)) {
			this.currentLanguage = language;
		} else {
			throw new Error(`Language ${language} is not supported.`);
		}
	}

	getAvailableLanguages(): string[] {
		return this.translationProvider.getAvailableLanguages();
	}
}
